import type { PersonalityTrait } from 'generated/openapi';
import { z } from 'zod';

const personalityTraitsEnum = z.enum<PersonalityTrait[]>([
  'AGGRESSIVE',
  'CALCULATED',
  'CALM',
  'CHAOTIC',
  'CHILL',
  'CREATIVE',
  'EMOTIONAL',
  'FUN',
  'HYPE',
  'INTENSE',
  'LEADER',
  'LOW_KEY',
  'PASSIONATE',
  'POSITIVE',
  'SUPPORTIVE',
]);

export interface PersonalityTraitOption {
  id: PersonalityTrait;
  label: string;
}

export const PERSONALITY_TRAIT_MAP = new Map<
  PersonalityTrait,
  PersonalityTraitOption
>([
  ['CHILL', { id: 'CHILL', label: '🧊 Chill' }],
  ['HYPE', { id: 'HYPE', label: '⚡ Hype' }],
  ['POSITIVE', { id: 'POSITIVE', label: '✨ Positive' }],
  ['INTENSE', { id: 'INTENSE', label: '🔥 Intense' }],
  ['FUN', { id: 'FUN', label: '😎 Fun' }],

  ['LOW_KEY', { id: 'LOW_KEY', label: '🤫 Low-key' }],
  ['LEADER', { id: 'LEADER', label: '🎯 Leader' }],
  ['CALM', { id: 'CALM', label: '🧘 Calm' }],
  ['EMOTIONAL', { id: 'EMOTIONAL', label: '😰 Emotional' }],
  ['PASSIONATE', { id: 'PASSIONATE', label: '❤️‍🔥 Passionate' }],

  ['CHAOTIC', { id: 'CHAOTIC', label: '🎲 Chaotic' }],
  ['CREATIVE', { id: 'CREATIVE', label: '🎨 Creative' }],
  ['CALCULATED', { id: 'CALCULATED', label: '📐 Calculated' }],
  ['AGGRESSIVE', { id: 'AGGRESSIVE', label: '🚀 Aggressive' }],
  ['SUPPORTIVE', { id: 'SUPPORTIVE', label: '🤝 Supportive' }],
]);

export const PERSONALITY_TRAIT_OPTIONS: PersonalityTraitOption[] = Array.from(
  PERSONALITY_TRAIT_MAP.values(),
);

// Category groupings based on Figma design
export const PERSONALITY_TRAIT_VIBE: PersonalityTrait[] = [
  'CHILL',
  'HYPE',
  'POSITIVE',
  'INTENSE',
  'FUN',
];

export const PERSONALITY_TRAIT_TEMPER: PersonalityTrait[] = [
  'LOW_KEY',
  'LEADER',
  'CALM',
  'EMOTIONAL',
  'PASSIONATE',
];

export const PERSONALITY_TRAIT_PLAYSTYLE: PersonalityTrait[] = [
  'CHAOTIC',
  'CREATIVE',
  'CALCULATED',
  'AGGRESSIVE',
  'SUPPORTIVE',
];

export const PERSONALITY_TRAIT_CATEGORIES = {
  vibe: PERSONALITY_TRAIT_VIBE,
  temper: PERSONALITY_TRAIT_TEMPER,
  playstyle: PERSONALITY_TRAIT_PLAYSTYLE,
} as const;

export const personalityTraitsSchema = z.object({
  vibe: z
    .array(personalityTraitsEnum)
    .min(1, { message: 'Pick at least one vibe trait' })
    .max(2, { message: 'Pick at most 2 vibe traits' }),
  temper: z
    .array(personalityTraitsEnum)
    .min(1, { message: 'Pick at least one temper trait' })
    .max(2, { message: 'Pick at most 2 temper traits' }),
  playstyle: z
    .array(personalityTraitsEnum)
    .min(1, { message: 'Pick at least one playstyle trait' })
    .max(2, { message: 'Pick at most 2 playstyle traits' }),
});
